import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable, of } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { DicomConfig } from "../../../common/dicom-config";
import { Device, DeviceLight } from "../../models/device";
import { DeviceCreate } from "../../models/device.create";
import { HttpClient } from "@angular/common/http";
import { filter, uniqBy } from "lodash";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";

@Injectable()
export class DeviceBackend {
  constructor(private http: HttpClient) {}

  load(): Observable<Device[]> {
    return this.http.get<Device[]>(
      `${environment.snoots.api.diagnostic}/devices`,
      {
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  loadLight(): Observable<DeviceLight[]> {
    return this.http.get<DeviceLight[]>(
      `${environment.snoots.api.diagnostic}/devices`,
      {
        headers: {
          "snt-product": "rocket"
        },
        params: {
          light: true
        }
      }
    );
  }

  loadDeviceById(id: string): Observable<Device> {
    return this.http.get<Device>(
      `${environment.snoots.api.diagnostic}/devices/${id}`,
      {
        headers: {
          "snt-product": "rocket"
        }
      }
    );
  }

  loadLightDeviceById(id: string): Observable<DeviceLight> {
    return this.http.get<DeviceLight>(
      `${environment.snoots.api.diagnostic}/devices/${id}`,
      {
        headers: {
          "snt-product": "rocket"
        },
        params: {
          light: true
        }
      }
    );
  }

  loadDeviceByModality(filterType: string): Observable<Device[]> {
    return this.http
      .get<Device[]>(`${environment.snoots.api.diagnostic}/devices`)
      .pipe(map((s) => filter(s, (x) => x.modality === filterType)));
  }

  loadDeviceLightByModality(filterType: string): Observable<DeviceLight[]> {
    return this.http
      .get<DeviceLight[]>(`${environment.snoots.api.diagnostic}/devices`, {
        params: { light: true }
      })
      .pipe(map((s) => filter(s, (x) => x.modality === filterType)));
  }

  @UserNotify({
    successMessage: marker("Dispositivo creato"),
    errorMessage: marker("Non è stato possibile creare il dispositivo")
  })
  create(data: DeviceCreate): Observable<Device> {
    return this.http.post<Device>(
      `${environment.snoots.api.diagnostic}/devices`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Dispositivo aggiornato"),
    errorMessage: marker("Non è stato possibile aggiornare il dispositivo")
  })
  update(data: DeviceCreate, id: string): Observable<Device> {
    return this.http.patch<Device>(
      `${environment.snoots.api.diagnostic}/devices/${id}`,
      data
    );
  }

  @UserNotify({
    successMessage: marker("Dispositivo cancellato"),
    errorMessage: marker("Non è stato possibile cancellare il dispositivo")
  })
  delete(id: string): Observable<string> {
    return this.http.delete<string>(
      `${environment.snoots.api.diagnostic}/devices/${id}`
    );
  }

  loadModalities(
    dicomConfigValues: DicomConfig[]
  ): Observable<{ name: string; value: string }[]> {
    return this.load().pipe(
      map((response) => {
        return response.map((devices) => {
          return {
            value: devices.modality,
            name: `${
              dicomConfigValues.find((x) => x.value === devices.modality)
                ?.display
            } (${devices.modality})`
          };
        });
      }),
      map((list) => uniqBy(list, (x) => x.value))
    );
  }

  loadLightModalities(
    dicomConfigValues: DicomConfig[]
  ): Observable<{ name: string; value: string }[]> {
    return this.loadLight().pipe(
      map((response) => {
        return response.map((devices) => {
          return {
            value: devices.modality,
            name: `${
              dicomConfigValues.find((x) => x.value === devices.modality)
                ?.display
            } (${devices.modality})`
          };
        });
      }),
      map((list) => uniqBy(list, (x) => x.value))
    );
  }
}
